/**
 * Shared Cast receiver / white-label contracts for TV and native apps.
 *
 * Web Chromecast/AirPlay can operate without a custom TV channel.
 * Roku, Fire TV, Samsung (Tizen), and LG (webOS) require a tenant-branded
 * receiver (or native shell) before discovery/play is operational.
 *
 * Do NOT claim those platforms work just because a provider class exists.
 */

import { CastingCapabilityState } from "./CastingEvents";

/** Content / media types accepted on the shared receiver payload. */
export var CastContentType = Object.freeze({
  VIDEO: "video",
  LIVE: "live",
  AUDIO: "audio",
});

/**
 * Playback states mirrored on the receiver session model.
 */
export var CastReceiverPlaybackState = Object.freeze({
  IDLE: "idle",
  LOADING: "loading",
  PLAYING: "playing",
  PAUSED: "paused",
  BUFFERING: "buffering",
  ENDED: "ended",
  ERROR: "error",
});

/**
 * Provider ids used across sender, admin config, and TV apps.
 */
export var CastProviderId = Object.freeze({
  CHROMECAST: "chromecast",
  AIRPLAY: "airplay",
  ROKU: "roku",
  FIRE_TV: "firetv",
  SAMSUNG: "samsung",
  LG: "lg",
  FUTURE_NATIVE: "future_native",
  SIMULATED: "simulated",
});

/**
 * Normalize white-label Cast branding from host config.
 * Never hard-codes one tenant product name as the only brand.
 *
 * @param {object} [raw]
 * @returns {object}
 */
export function normalizeWhiteLabelCastConfig(raw) {
  raw = raw || {};
  var supported = raw.supportedCastProviders;
  if (!Array.isArray(supported) || !supported.length) {
    supported = [
      CastProviderId.CHROMECAST,
      CastProviderId.AIRPLAY,
      CastProviderId.ROKU,
      CastProviderId.FIRE_TV,
      CastProviderId.SAMSUNG,
      CastProviderId.LG,
      CastProviderId.FUTURE_NATIVE,
    ];
  }
  return {
    applicationKey: raw.applicationKey || raw.application_key || "",
    applicationName: raw.applicationName || raw.application_name || "",
    receiverApplicationId:
      raw.receiverApplicationId ||
      raw.cast_receiver_application_id ||
      raw.chromecast_receiver_id ||
      "",
    supportedCastProviders: supported.slice(),
    logo: raw.logo || raw.cast_logo || "",
    defaultReceiver: raw.defaultReceiver || raw.default_receiver || "chromecast",
    rokuChannelId: raw.rokuChannelId || raw.roku_channel_id || "",
    fireTvPackageName: raw.fireTvPackageName || raw.fire_tv_package_name || "",
    samsungAppId: raw.samsungAppId || raw.samsung_app_id || "",
    lgAppId: raw.lgAppId || raw.lg_app_id || "",
    futureNativeBridge:
      raw.futureNativeBridge || raw.future_native_bridge || "",
  };
}

/**
 * Shared session model exchanged between web sender and TV / native receivers.
 * Media URLs must be short-lived / authorized playback refs — never long-lived
 * private credentials or unauthorized CDN URLs.
 *
 * @param {object} [partial]
 * @returns {object}
 */
export function createCastReceiverSession(partial) {
  partial = partial || {};
  var media = partial.media || {};
  return {
    sessionId: partial.sessionId || "",
    application: {
      applicationKey:
        (partial.application && partial.application.applicationKey) ||
        partial.applicationKey ||
        "",
      applicationName:
        (partial.application && partial.application.applicationName) ||
        partial.applicationName ||
        "",
      logo: (partial.application && partial.application.logo) || partial.logo || "",
      receiverApplicationId:
        (partial.application && partial.application.receiverApplicationId) ||
        partial.receiverApplicationId ||
        "",
    },
    contentId: partial.contentId || "",
    contentType: partial.contentType || CastContentType.VIDEO,
    title: partial.title || "",
    artwork: partial.artwork || partial.poster || "",
    media: {
      // Authorized playback reference (HLS/DASH/tokenized URL), not raw secrets
      mediaRef: media.mediaRef || media.src || "",
      contentType: media.contentType || "",
      drm: media.drm || null,
    },
    position: partial.position != null ? partial.position : 0,
    duration: partial.duration != null ? partial.duration : 0,
    volume: partial.volume != null ? partial.volume : 1,
    muted: !!partial.muted,
    state: partial.state || CastReceiverPlaybackState.IDLE,
    provider: partial.provider || "",
    updatedAt: partial.updatedAt || new Date().toISOString(),
  };
}

/**
 * Build a load/control command for native TV apps (Roku / Fire / Tizen / webOS).
 * Controllers must authenticate the session owner; never accept cross-user control.
 *
 * @param {string} action play|pause|stop|seek|load|setVolume|disconnect
 * @param {object} session createCastReceiverSession()
 * @param {object} [extra]
 * @returns {object}
 */
export function createReceiverControlMessage(action, session, extra) {
  extra = extra || {};
  return {
    protocolVersion: 1,
    action: action,
    sessionId: session && session.sessionId,
    applicationKey:
      session && session.application && session.application.applicationKey,
    contentId: session && session.contentId,
    position: extra.position != null ? extra.position : session && session.position,
    volume: extra.volume != null ? extra.volume : session && session.volume,
    media: session && session.media,
    title: session && session.title,
    artwork: session && session.artwork,
    auth: {
      // Existing app auth token / playback grant — never embed private long-lived keys
      playbackGrant: extra.playbackGrant || null,
      userScoped: true,
    },
    issuedAt: new Date().toISOString(),
  };
}

/**
 * Phase 2 receiver experience surfaces (full Pentecost+ / white-label on TV).
 * Phase 1 only requires video playback; these are the nav targets for Phase 2.
 */
export var CastReceiverExperienceSurfaces = Object.freeze([
  "home",
  "series",
  "movies",
  "kids",
  "guide",
  "browse",
  "organizations",
  "creators",
  "playback",
  "account",
]);

/**
 * Platform-specific receiver requirements (honest status for docs + UI).
 */
export var CastPlatformReceiverRequirements = Object.freeze({
  chromecast: {
    provider: CastProviderId.CHROMECAST,
    operationalOnWeb: true,
    requiresReceiverApp: false,
    discovery: "Google Cast CAF / Cast SDK device list + system picker",
    receiver: "Default CAF media receiver or white-label receiverApplicationId",
    playback: "loadMedia via Cast session (web operational)",
    note:
      "Google Cast CAF sender works in Chrome / Edge / supported browsers. Optional custom receiver via white-label receiverApplicationId.",
  },
  airplay: {
    provider: CastProviderId.AIRPLAY,
    operationalOnWeb: true,
    requiresWebKit: true,
    discovery: "WebKit remote playback / AirPlay picker (Safari/iOS/macOS only)",
    receiver: "Apple TV / AirPlay-capable display (system)",
    playback: "HTMLMediaElement remote playback (WebKit only)",
    note:
      "AirPlay picker requires Safari / iOS / macOS WebKit. Not available in Chrome on Windows.",
  },
  roku: {
    provider: CastProviderId.ROKU,
    operationalOnWeb: false,
    requiresReceiverApp: true,
    discovery:
      "Not from browser. ECP SSDP/query is LAN-only and CORS-blocked. Production uses cloud session + pairing / same-account claim, or LAN ECP from a native shell.",
    receiver: "Branded Roku channel (BrightScript/SceneGraph) required",
    playback:
      "Channel loads CastReceiverSession (cloud claim or deep-link contentId) then plays authorized mediaRef",
    mechanism:
      "Roku External Control Protocol (ECP) is LAN-only and not CORS-safe from arbitrary websites. Production cast requires a branded Roku channel that accepts the shared CastReceiverSession over an authenticated cloud/deep-link channel.",
    ecpLaunchTemplate:
      "http://{rokuIp}:8060/launch/{channelId}?contentId={sessionOrContentId}&mediaType={mediaType}",
    ecpInputTemplate:
      "http://{rokuIp}:8060/input?contentId={sessionOrContentId}&mediaType={mediaType}",
    contractFields: [
      "sessionId",
      "applicationKey",
      "contentId",
      "media.mediaRef",
      "title",
      "artwork",
      "position",
      "state",
      "pairingCode",
    ],
  },
  firetv: {
    provider: CastProviderId.FIRE_TV,
    operationalOnWeb: false,
    requiresReceiverApp: true,
    discovery: "Not from browser SPA; Fire TV app / Amazon DIAL or account-linked cloud session",
    receiver: "Branded Fire TV (Android) application required",
    playback: "App receives CastReceiverSession + control messages",
    mechanism:
      "Amazon Fire TV is not Chromecast. Web senders cannot claim Fire TV cast without a Fire TV application that receives CastReceiverSession payloads (contentId, mediaRef, title, artwork, position, play/pause/seek, sessionId) under existing auth.",
    contractFields: [
      "sessionId",
      "contentId",
      "media.mediaRef",
      "title",
      "artwork",
      "position",
      "action",
    ],
  },
  samsung: {
    provider: CastProviderId.SAMSUNG,
    operationalOnWeb: false,
    requiresReceiverApp: true,
    discovery: "Not from browser SPA without Tizen / Smart View companion",
    receiver: "Branded Tizen TV application required",
    playback: "Tizen app loads CastReceiverSession",
    mechanism:
      "Samsung Tizen TVs need a Tizen receiver / Smart View companion path. Browser pages cannot honestly discover or control Samsung TVs without that app.",
  },
  lg: {
    provider: CastProviderId.LG,
    operationalOnWeb: false,
    requiresReceiverApp: true,
    discovery: "Not from browser SPA; webOS app or native Connect SDK shell",
    receiver: "Branded webOS TV application required",
    playback: "webOS app loads CastReceiverSession",
    mechanism:
      "LG webOS TVs need a webOS app (or Connect SDK path inside a native shell). Pure web discovery/control is not supported from this SPA.",
  },
  future_native: {
    provider: CastProviderId.FUTURE_NATIVE,
    operationalOnWeb: false,
    requiresNativeApp: true,
    discovery: "Injected by native shell (iOS/Android/tvOS/Android TV)",
    receiver: "Native cast bridge implementing CastingProvider contract",
    playback: "Delegated to native bridge",
    mechanism:
      "Future iOS / Android / tvOS / Android TV shells bridge into CastingManager via a native module implementing the same provider contract.",
  },
});

/**
 * Honest platform status row builder for docs / diagnostics.
 * physicalQa values: "not_run" | "partial" | "pass" | "blocked_no_receiver" | "blocked_no_hardware"
 *
 * @param {string} providerId
 * @param {object} [overrides]
 * @returns {object}
 */
export function buildPlatformStatusRow(providerId, overrides) {
  overrides = overrides || {};
  var req = CastPlatformReceiverRequirements[providerId] || {};
  var status = "Foundation Only";
  if (providerId === CastProviderId.CHROMECAST) {
    status = "Implemented / Requires Physical QA";
  } else if (providerId === CastProviderId.AIRPLAY) {
    status =
      "Implemented on WebKit · Unsupported elsewhere / Requires Physical QA";
  } else if (req.requiresReceiverApp) {
    status = "Foundation Only / Requires Native Receiver";
  } else if (req.requiresNativeApp) {
    status = "Foundation Only / Requires Native App";
  }
  return {
    platform: providerId,
    discovery: overrides.discovery || req.discovery || "unknown",
    receiver: overrides.receiver || req.receiver || "unknown",
    playback: overrides.playback || req.playback || "unknown",
    physicalQa: overrides.physicalQa || "not_run",
    status: overrides.status || status,
  };
}

/**
 * Map capability state → whether UI may show Connected / Casting chrome.
 * @param {string} capabilityState
 * @returns {boolean}
 */
export function isReallyConnectedCapability(capabilityState) {
  return (
    capabilityState === CastingCapabilityState.CONNECTED ||
    capabilityState === CastingCapabilityState.PLAYING ||
    capabilityState === CastingCapabilityState.PAUSED ||
    capabilityState === CastingCapabilityState.CONNECTING
  );
}

export default {
  CastContentType: CastContentType,
  CastReceiverPlaybackState: CastReceiverPlaybackState,
  CastProviderId: CastProviderId,
  CastReceiverExperienceSurfaces: CastReceiverExperienceSurfaces,
  CastPlatformReceiverRequirements: CastPlatformReceiverRequirements,
  normalizeWhiteLabelCastConfig: normalizeWhiteLabelCastConfig,
  createCastReceiverSession: createCastReceiverSession,
  createReceiverControlMessage: createReceiverControlMessage,
  isReallyConnectedCapability: isReallyConnectedCapability,
  buildPlatformStatusRow: buildPlatformStatusRow,
};
